import React, { useEffect, useState } from "react";
import { authErrorMessage } from "@/util/authError";

const RESEND_COOLDOWN_SECONDS = 60;

interface EmailSentPanelProps {
  email: string;
  message: React.ReactNode;
  onResend: () => Promise<void>;
}

const EmailSentPanel = ({ email, message, onResend }: EmailSentPanelProps) => {
  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const resend = async () => {
    setResending(true);
    setResendError(null);
    try {
      await onResend();
      setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      setResendError(authErrorMessage(error));
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (secondsLeft <= 0) {
      return;
    }
    const timer = window.setTimeout(() => setSecondsLeft((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const resendLabel = resending ? "Sending…" : secondsLeft > 0 ? `Resend email in ${secondsLeft}s` : "Resend email";

  return (
    <div className="rounded-xl border border-[#e0efec] bg-[#f2fbf9] p-5">
      <p className="text-[15px] leading-relaxed text-navy">{message}</p>
      <p className="mt-1 text-[15px] font-semibold break-all text-navy">{email}</p>
      <p className="mt-3 text-[13px] leading-relaxed text-slate-500">
        The link works once and expires in 1 hour. If it doesn't arrive in a few minutes, check your spam folder.
      </p>
      <button
        type="button"
        onClick={resend}
        disabled={secondsLeft > 0 || resending}
        className="mt-4 cursor-pointer text-[15px] font-semibold text-brand-ink-strong transition-colors hover:text-navy disabled:cursor-default disabled:text-slate-400"
      >
        {resendLabel}
      </button>
      {resendError && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {resendError}
        </p>
      )}
    </div>
  );
};

export default EmailSentPanel;
